/**
 * LLMVoice — the primary voice backend (the intended mode, CLAUDE.md). Routes a
 * batched VoiceRequest through the thin server proxy (POST <base>/api/chat) with
 * the bearer token, parses the ChatApiResponse, safety-filters the model's output,
 * and caches identical batches. It NEVER throws on degradation: any failure
 * (no token / network / non-2xx / malformed body / empty result) gracefully falls
 * back to the TemplateVoice floor so the town is always populated + warm.
 *
 * Voice never mutates canon: lines whose speaker isn't in the request are dropped,
 * and the text is scrubbed but the structured beat facts are never echoed back as
 * new truth.
 */
import type {
  Voice,
  VoiceRequest,
  VoiceResponse,
  VoiceLine,
  ChatApiRequest,
  ChatApiResponse,
} from '@/shared';
import { hashString } from '@/core';
import { TemplateVoice } from './templateVoice.js';
import { scrubLine, softenEmotion } from './safety.js';

/** Minimal fetch shape we depend on (injectable for tests / SSR-safety). */
export type FetchLike = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
  },
) => Promise<Response>;

export interface LLMVoiceConfig {
  /** Base URL of the proxy (env-configurable; localhost:8787 in dev). */
  readonly apiBaseUrl: string;
  /** Bearer token exchanged for the app password. Null ⇒ cannot call the proxy. */
  readonly token: string | null;
  /** Injectable fetch (defaults to global fetch). */
  readonly fetchImpl?: FetchLike;
  /** The template floor to degrade to. Defaults to a fresh TemplateVoice. */
  readonly fallback?: Voice;
  /** Per-request timeout in ms (default 12s; town must never hang). */
  readonly timeoutMs?: number;
  /** Max cached exchanges kept in memory (LRU-ish, bounded). */
  readonly cacheLimit?: number;
}

const DEFAULT_TIMEOUT_MS = 12_000;
const DEFAULT_CACHE_LIMIT = 256;

export class LLMVoice implements Voice {
  readonly kind = 'llm' as const;
  private readonly config: LLMVoiceConfig;
  private readonly fetchImpl: FetchLike | null;
  private readonly fallback: Voice;
  private readonly cache = new Map<string, VoiceResponse>();

  constructor(config: LLMVoiceConfig) {
    this.config = config;
    this.fetchImpl =
      config.fetchImpl ?? (typeof fetch === 'function' ? (fetch as unknown as FetchLike) : null);
    this.fallback = config.fallback ?? new TemplateVoice(0);
  }

  /** The configured proxy base URL (e.g. for cost-transparency UI). */
  get apiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }

  async generate(request: VoiceRequest): Promise<VoiceResponse> {
    // No token / no fetch ⇒ we cannot authenticate against the proxy at all.
    if (!this.config.token || !this.fetchImpl) {
      return this.degrade(request);
    }

    const key = this.cacheKey(request);
    const cached = this.cache.get(key);
    if (cached) return cached;

    const response = await this.callProxy(request).catch(() => null);
    if (!response) return this.degrade(request);

    this.remember(key, response);
    return response;
  }

  // ── proxy call ───────────────────────────────────────────────────────────

  private async callProxy(request: VoiceRequest): Promise<VoiceResponse | null> {
    const fetchImpl = this.fetchImpl!;
    const body: ChatApiRequest = {
      mode: 'chat',
      speakers: request.speakers,
      beats: request.beats,
      player: request.player,
      desiredLineCount: request.desiredLineCount,
    };

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer =
      controller != null
        ? setTimeout(() => controller.abort(), this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS)
        : null;

    try {
      const res = await fetchImpl(this.endpoint(), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${this.config.token}`,
        },
        body: JSON.stringify(body),
        ...(controller ? { signal: controller.signal } : {}),
      });

      if (!res.ok) return null;

      const parsed = (await res.json()) as ChatApiResponse;
      return this.toVoiceResponse(parsed, request);
    } catch {
      // Network error, abort, or malformed JSON ⇒ caller degrades. Never throw.
      return null;
    } finally {
      if (timer != null) clearTimeout(timer);
    }
  }

  private toVoiceResponse(parsed: ChatApiResponse, request: VoiceRequest): VoiceResponse | null {
    if (!parsed || !Array.isArray(parsed.lines)) return null;

    const allowed = new Set(request.speakers.map((s) => s.trainerId));
    const lines: VoiceLine[] = [];
    for (const raw of parsed.lines) {
      if (!raw || typeof raw.text !== 'string' || !raw.text.trim()) continue;
      if (!allowed.has(raw.trainerId)) continue; // never invent speakers (canon-safe)
      const text = scrubLine(raw.text).trim();
      if (!text) continue;
      lines.push({
        trainerId: raw.trainerId,
        emotion: softenEmotion(raw.emotion),
        text,
      });
    }

    // Zero usable lines ⇒ treat as a miss so we degrade to the warm floor.
    if (lines.length === 0) return null;

    return {
      lines,
      source: 'llm',
      tokensUsed: Number.isFinite(parsed.tokensUsed) ? parsed.tokensUsed : 0,
    };
  }

  // ── degradation + cache ────────────────────────────────────────────────────

  private async degrade(request: VoiceRequest): Promise<VoiceResponse> {
    // The floor never throws; if it somehow did, we still hand back an empty,
    // valid template response rather than erroring out the town.
    try {
      return await this.fallback.generate(request);
    } catch {
      return { lines: [], source: 'template', tokensUsed: 0 };
    }
  }

  private remember(key: string, response: VoiceResponse): void {
    if (this.cache.size >= (this.config.cacheLimit ?? DEFAULT_CACHE_LIMIT)) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) this.cache.delete(oldest);
    }
    this.cache.set(key, response);
  }

  private endpoint(): string {
    const base = this.config.apiBaseUrl.replace(/\/+$/, '');
    return `${base}/api/chat`;
  }

  private cacheKey(request: VoiceRequest): string {
    const beats = request.beats.map((b) => `${b.id}:${b.kind}:${b.subjectTrainerId}`).join('|');
    const speakers = request.speakers.map((s) => s.trainerId).join('|');
    const player = `${request.player.playerName}:${request.player.recentDeeds.join(',')}`;
    return String(hashString(`${beats}#${speakers}#${player}#${request.desiredLineCount}`));
  }
}
